import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import styles from './date-range-picker.module.css';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function addIsoDays(isoDate: string, days: number): string {
  const [yearStr, monthStr, dayStr] = isoDate.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

function minEndDate(startValue: string, exclusiveEnd: boolean): string | undefined {
  if (!ISO_DATE.test(startValue)) return undefined;
  return exclusiveEnd ? addIsoDays(startValue, 1) : startValue;
}

interface DateRangePickerProps {
  startId: string;
  endId: string;
  startLabel: string;
  endLabel: string;
  startValue: string;
  endValue: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  startError?: ReactNode;
  endError?: ReactNode;
  startDescribedBy?: string;
  endDescribedBy?: string;
  required?: boolean;
  className?: string;
  /** Inclusive end (season) vs exclusive end (stay/block). Used only for the end field helper. */
  endHelperText?: ReactNode;
  /**
   * Half-open ranges (arrival/departure, availability blocks): the end date must be
   * after the start date, and choosing a start date fills an empty or invalid end
   * with the following day.
   */
  exclusiveEnd?: boolean;
}

export function DateRangePicker({
  startId,
  endId,
  startLabel,
  endLabel,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  startError,
  endError,
  startDescribedBy,
  endDescribedBy,
  required,
  className,
  endHelperText,
  exclusiveEnd = false,
}: DateRangePickerProps) {
  const endMin = minEndDate(startValue, exclusiveEnd);

  function handleStartChange(value: string) {
    onStartChange(value);
    const nextMin = minEndDate(value, exclusiveEnd);
    if (nextMin && (!endValue || endValue < nextMin)) {
      onEndChange(nextMin);
    }
  }

  function handleEndChange(value: string) {
    if (endMin && value && value < endMin) {
      onEndChange(endMin);
      return;
    }
    onEndChange(value);
  }

  return (
    <div className={cn(styles.root, className)}>
      <Input
        id={startId}
        type="date"
        label={startLabel}
        value={startValue}
        onChange={(event) => handleStartChange(event.target.value)}
        error={startError}
        required={required}
        aria-describedby={startDescribedBy}
      />
      <Input
        id={endId}
        type="date"
        label={endLabel}
        value={endValue}
        min={endMin}
        onChange={(event) => handleEndChange(event.target.value)}
        error={endError}
        helperText={endHelperText}
        required={required}
        aria-describedby={endDescribedBy}
      />
    </div>
  );
}
